import type { Alert } from "@/shared/api/models";

/**
 * 기준선 예외 — 「경보 목록을 처음 받았다」를 「이미 본 것」으로 흡수할 때 **빼 둘** 경보(2026-10-19, 10-12 검증 V-22).
 *
 * AlarmCenter(경보창)와 AlertTtsAnnouncer(음성)는 화면이 처음 뜰 때 받은 미해제 경보를 통째로 기준선에 넣고
 * 그 뒤에 새로 나타난 경보만 띄우고 읽는다 — 2026-08-31 「화면을 열 때마다 과거 경보 12건 낭독」 사고의 대책이다.
 * 그런데 그 때문에 **푸시를 눌러 들어오거나 화면을 새로 고친 순간 진행 중인 경보**까지 조용히 묻혔다.
 * 두 화면이 같은 규칙을 쓰도록 판정을 여기 한 곳에 둔다.
 *
 *   1. 푸시 알림으로 고른 경보(`focusId`) — 기본으로 켠다. 사용자가 그 경보를 보려고 누른 것이다.
 *      단 **아직 미확인(open)·미해제일 때만**({@link isPushFocusEligible}, pushFocus.ts 「아직 미확인이면」).
 *      경보창은 open 만 띄우므로, 이미 확인(acknowledged)된 경보까지 예외로 두면 낭독기만 사이렌·「즉시 대피」를
 *      다시 내고 창은 뜨지 않아 화면과 소리가 어긋났다(10-20 F1 검증 잔존 결함 1). 확인된 경보는 화면 이동만 한다.
 *   2.「다시 열 때 진행 중 경보 알림」 설정(useAlarmSettings.reshowRecent, **기본 끔** — 재표시 정책은 사용자 결정)이
 *      켜져 있으면, 미확인·경계 이상·최근 {@link RESHOW_RECENT_MIN}분 안에 난 경보.
 *
 * ⚠️ 2번만 기기 시계를 본다. 그래서 시계가 틀리면 **예외가 줄어드는 쪽**으로 기울게 했다: 발령 시각이 이 기기보다
 * 1분 넘게 앞서면(시계가 늦음) 예외로 두지 않는다. 시계가 빠르면 모든 경보가 오래돼 보여 예외가 없다 — 어느 쪽이든
 * 설정을 끈 지금 동작과 같아질 뿐 백로그를 읽지는 않는다. 다만 시계가 N분 이상 크게 늦으면 그만큼 지난 경보가
 * 최근으로 보일 수 있다(그때도 낭독은 틱당 3묶음 상한에 걸린다).
 */

/** 다시 열 때 재표시하는 창(분). 정책 결정 전 제안값(10-12 검증 조치계획). */
export const RESHOW_RECENT_MIN = 10;

type BaselineAlert = Pick<Alert, "id" | "state" | "level" | "raised_at"> & { cleared_at?: string | null };

/** 「다시 열 때」 재표시 후보 — 미확인(open)·미해제·경계 이상·최근 N분 안. */
export function isReshowCandidate(a: BaselineAlert, now: number): boolean {
  if (a.state !== "open" || a.cleared_at) return false;
  if ((a.level ?? 0) < 2) return false;
  const t = a.raised_at ? Date.parse(a.raised_at) : NaN;
  if (Number.isNaN(t)) return false;
  const age = now - t;
  return age >= -60_000 && age <= RESHOW_RECENT_MIN * 60_000;
}

export interface BaselineOptions {
  /** 푸시 알림으로 고른 경보 id(없으면 null). */
  focusId: number | null;
  /** 「다시 열 때 진행 중 경보 알림」 설정. */
  reshowRecent: boolean;
  now: number;
}

/**
 * 푸시 알림으로 고른 경보를 「처음 뜨는 경보」처럼 다시 띄우고 읽어도 되는가 — 아직 미확인(open)·미해제일 때만.
 * 콜드 스타트(기준선 예외)와 앱이 살아 있을 때(흡수 해제) 둘 다 이 판정을 쓴다(AlertTtsAnnouncer·AlarmCenter).
 */
export function isPushFocusEligible(a: Pick<BaselineAlert, "state"> & { cleared_at?: string | null }): boolean {
  return a.state === "open" && !a.cleared_at;
}

/** 기준선에 넣지 말아야 하는가(= 첫 목록이라도 띄우고 읽을 경보인가). */
export function isBaselineExempt(a: BaselineAlert, opts: BaselineOptions): boolean {
  if (a.id != null && opts.focusId != null && a.id === opts.focusId && isPushFocusEligible(a)) return true;
  return opts.reshowRecent && isReshowCandidate(a, opts.now);
}
